import React, { useEffect, useMemo, useRef, useState } from "react";
import { Backdrop, CircularProgress } from "@mui/material";
import { Box, Modal } from "@mui/material";
import Typography from "@mui/material/Typography";
import { JSONTree } from "react-json-tree";
import html2pdf from "html2pdf.js";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { comparisonWorkbook } from "../../../utils/comparisonWorkbook";
import { saveAs } from "file-saver";

//component
import useDynamicTable from "../../../hooks/useDynamicTable";
import BrandLogo from "../../../components/common/BrandLogo/BrandLogo";
import DButton from "../../../components/Dashboard/DashboardButtons/Dbutton";
import DButtonOutlined from "../../Dashboard/DashboardButtons/dButtonOutlined";
//css
import classes from "./comparetable.module.css";
import dashboardClasses from "../../../pages/DashboardStyles/DashboardStyles.module.css";
//asset
import logoImg from "../../../assets/images/svg/Logo.svg";
import rightArrowIcon from "../../../assets/images/svg/view-details-right-arrow-blue.svg";
import refreshIcon from "../../../assets/images/svg/refreshIcon.svg";
import DropdownBtnOutlined from "../../Dashboard/DropdownButtons/DropdownBtnOutlined/DropdownBtnOutlined";
import DropdownBtn from "../../Dashboard/DropdownButtons/DropdownBtn/DropdownBtn";
const ViewButtom = ({ label, showIcon, icon, buttonClass, viewDetails }) => {
  return (
    <button className={buttonClass || ""} onClick={viewDetails}>
      {label}
      {showIcon && (
        <img src={icon} className={classes.btnIcon} alt="arrow icon" />
      )}
    </button>
  );
};

const ComparedCard = ({ data, viewDetails }) => {
  const { Input_data, Output_data, Title } = data;
  let title = Title;
  let inputDataParse = Input_data;
  let outputDataParse;
  let outputDataParseError;
  try {
    outputDataParse = JSON.parse(Output_data);
  } catch (error) {
    outputDataParseError = Output_data;
  }
  return (
    <>
      {inputDataParse && (
        <div className={classes.comparedFileItem}>
          <p className={classes.comparedFileTitle}>{data.Title} INPUT</p>
          <ViewButtom
            label="VIEW DETAILS"
            showIcon={true}
            icon={rightArrowIcon}
            buttonClass={classes.viewDetailsBtn}
            viewDetails={() => viewDetails(inputDataParse, "text", title)}
          />
        </div>
      )}
      {outputDataParse ? (
        <div className={classes.comparedFileItem}>
          <p className={classes.comparedFileTitle}>{data.Title} OUTPUT</p>
          <ViewButtom
            label="VIEW DETAILS"
            showIcon={true}
            icon={rightArrowIcon}
            buttonClass={classes.viewDetailsBtn}
            viewDetails={() => viewDetails(outputDataParse, "json", title)}
          />
        </div>
      ) : (
        <div className={classes.comparedFileItem}>
          <p className={classes.comparedFileTitle}>{data.Title} OUTPUT</p>
          <ViewButtom
            label="VIEW DETAILS"
            showIcon={true}
            icon={rightArrowIcon}
            buttonClass={classes.viewDetailsBtn}
            viewDetails={() =>
              viewDetails(outputDataParseError, "error", title)
            }
          />
        </div>
      )}
    </>
  );
};




const NOT_QUOTED_VALUES = new Set(["", "na", "n/a", "not available", "null", "undefined", "-"]);


const isQuotedValue = (value) =>
  value !== null &&
  value !== undefined &&
  !NOT_QUOTED_VALUES.has(String(value).trim().toLowerCase());

const displayValue = (value) =>
  isQuotedValue(value)
    ? typeof value === "object"
      ? JSON.stringify(value)
      : value
    : "Not Quoted";

const formatPrice = (value, currency) => {
  if (!isQuotedValue(value)) return "Not Quoted";
  const number = numericQuotePrice(value);
  if (number === null) return displayValue(value);

  const formatted = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: Number.isInteger(number) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(number);
  const currencyCode = quotePriceCurrency(value, currency) || "";
  const symbols = { INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "AED" };
  return `${symbols[currencyCode] || currencyCode || ""}${currencyCode === "AED" ? " " : ""}${formatted}`;
};

const normalizeComparisonItem = (item) => Object.fromEntries(
  Object.entries(item || {}).filter(([key]) => !/^evaluation(?:\b|_)/i.test(key.trim())).map(([key, value]) => [
    key.trim().toLowerCase().replace(/[\s-]+/g, "_"), value,
  ])
);

const buildGroupedComparison = (documents) => {
  const normalizeMatch = (value) => String(value ?? "").trim().toLowerCase().replace(/\s+/g, " ");
  const validItems = (items) => (Array.isArray(items) ? items : [])
    .filter((item) => item && typeof item === "object" && !Array.isArray(item))
    .map(normalizeComparisonItem);
  const rows = validItems(documents[0]).map((item) => ({
    specification: isQuotedValue(item.description) ? item.description : "Description not provided",
    items: [item],
    quotes: new Map(),
  }));
  const vendors = [];
  documents.slice(1).forEach((document, documentIndex) => {
    const items = validItems(document);
    const vendor = `quotation-${documentIndex + 1}`;
    const names = [...new Set(items.map((item) =>
      [item.vendor_name, item.vendor, item.company_name].find(isQuotedValue)
    ).filter(isQuotedValue))];
    vendors.push([vendor, names.join(" / ") || `Vendor ${documentIndex + 1} — name not provided`]);
    items.forEach((item) => {
      const matches = isQuotedValue(item.rfq_input) ? rows.filter((row) =>
        row.items.some((requirement) => isQuotedValue(requirement.description) &&
          normalizeMatch(requirement.description) === normalizeMatch(item.rfq_input))
      ) : [];
      let row = matches.length === 1 ? matches[0] : null;
      if (!row) {
        row = { specification: `Unmatched quotation${isQuotedValue(item.rfq_input) ? ` — ${item.rfq_input}` : " — RFQ reference not provided"}`, items: [], quotes: new Map() };
        rows.push(row);
      }
      if (!row.quotes.has(vendor)) row.quotes.set(vendor, []);
      row.quotes.get(vendor).push(item);
    });
  });
  return {
    vendors, rows,
    detailFields: ["brand", "currency", "warranty", "delivery_terms", "payment_terms"],
  };
};

const CompanyQuotationHeaders = ({ comparison, columns, rfqColumns }) => (
  <>
    <tr>
      <th colSpan={rfqColumns} rowSpan={2} scope="colgroup" className={classes.rfqGroupHead}>
        RFQ Specification Details
      </th>
      {comparison.companies.map(({ key, name, quotations }, index) => (
        <th key={key} colSpan={quotations.reduce((sum, quote) => sum + columns.get(quote.key).length, 0)}
          scope="colgroup" className={`${classes.vendorGroupHead} ${classes.companyHeading}`}
          data-vendor-tone={index % 2} data-vendor-start="true">
          <span className={classes.groupName}>{name}</span>
        </th>
      ))}
    </tr>
    <tr className={classes.quotationHeadingRow}>
      {comparison.companies.flatMap(({ quotations }, companyIndex) => quotations.map((quote, index) => (
        <th key={quote.key} colSpan={columns.get(quote.key).length} scope="colgroup"
          className={classes.quotationHeading} data-vendor-tone={companyIndex % 2} data-vendor-start="true">
          Quote {index + 1}
        </th>
      )))}
    </tr>
  </>
);

const getRecordQuotations = (record = {}) => {
  const directQuotes = [
    record?.quotations,
    record?.quotation_data,
    record?.quotationData,
    record?.quote_data,
    record?.quoteData,
    record?.quotes,
    record?.vendor_quotations,
  ];

  return directQuotes.flatMap((value) =>
    Array.isArray(value) ? value : value && typeof value === "object" ? [value] : [],
  ).filter(Boolean);
};

const addQuotationOutputFallback = (records, files = []) => {
  if (!Array.isArray(records)) return records;

  const normalizeMatch = (value) =>
    String(value ?? "").trim().toLowerCase().replace(/\s+/g, " ");
  const getVendorName = (quote = {}) => {
    const normalized = normalizeComparisonItem(quote);
    return [
      normalized.vendor_name,
      normalized.vendor,
      normalized.company_name,
      normalized.supplier_name,
      normalized.supplier,
      quote?.vendor_details?.vendor_details_name,
      quote?.vendor_details?.name,
      quote?.supplier_details?.name,
      quote?.company?.name,
    ].find(isQuotedValue);
  };
  const existingVendorKeys = new Set(
    records.flatMap((record) =>
      (record.quotations || [])
        .map(getVendorName)
        .filter(isQuotedValue)
        .map(normalizeMatch),
    ),
  );

  const quoteOutputs = (Array.isArray(files) ? files : []).flatMap((file, documentIndex) => {
    if (!/^quotation\s*\d*/i.test(String(file?.Title || "").trim())) return [];
    try {
      const output = typeof file?.Output_data === "string"
        ? JSON.parse(file.Output_data)
        : file?.Output_data;
      const values = Array.isArray(output) ? output : output && typeof output === "object" ? [output] : [];
      return values
        .filter(Boolean)
        .map((quote) => ({
          ...quote,
          vendor_name: getVendorName(quote),
          _comparison_document: documentIndex,
        }))
        .filter((quote) => isQuotedValue(quote.vendor_name));
    } catch {
      return [];
    }
  });

  if (!quoteOutputs.length) return records;
  const missingVendorKeys = new Set(
    quoteOutputs
      .map((quote) => normalizeMatch(quote.vendor_name))
      .filter((vendorKey) => !existingVendorKeys.has(vendorKey)),
  );
  const mergedRecords = records.map((record) => ({
    ...record,
    quotations: [...(record.quotations || [])],
  }));

  quoteOutputs.forEach((quote) => {
    if (!missingVendorKeys.has(normalizeMatch(quote.vendor_name))) return;
    const quoteReference = normalizeMatch(
      quote?.rfq_input || quote?.item_name || quote?.description,
    );
    const targetRecord =
      mergedRecords.find((record) => {
        const rfq = record?.rfq_data || {};
        return quoteReference && [rfq.item_name, rfq.description]
          .some((value) => normalizeMatch(value) === quoteReference);
      }) || mergedRecords[0];

    targetRecord?.quotations.push(quote);
  });

  return mergedRecords;
};

const getItemRecords = (data) => {
  const nestedRecords = data
    .flatMap((item) => (Array.isArray(item) ? item : [item]))
    .filter((item) => item?.rfq_data);

  if (nestedRecords.length) {
    const recordsByRfq = new Map();

    nestedRecords.forEach((item, index) => {
      const rfq = normalizeComparisonItem(item.rfq_data);
      const keyParts = [
        rfq.item_token,
        rfq.item_code,
        rfq.item_name,
        rfq.description,
      ]
        .filter(isQuotedValue)
        .map((value) => String(value).trim().toLowerCase())
      const key = keyParts.length ? keyParts.join("|") : `rfq-${index}`;
      const existing = recordsByRfq.get(key);

      if (existing) {
        existing.quotations.push(...getRecordQuotations(item));
      } else {
        recordsByRfq.set(key, {
          ...item,
          quotations: getRecordQuotations(item),
        });
      }
    });

    return [...recordsByRfq.values()];
  }
  const groups = data.slice(1).filter(Array.isArray);
  if (!groups.some((group) => group.some((item) => item?.rfq_data))) return null;
  const records = (Array.isArray(data[0]) ? data[0] : []).filter((item) => item && typeof item === "object")
    .map((item) => ({ rfq_data: item, quotations: [] }));
  const rfqItemCount = records.length;
  const normalize = (value) => isQuotedValue(value) ? String(value).trim().toLowerCase().replace(/\s+/g, " ") : "";
  groups.forEach((group, documentIndex) => {
    const matchedItems = new Set();
    group.forEach((record, itemIndex) => {
      if (!record?.rfq_data) return;
      const input = normalizeComparisonItem(record.rfq_data);
      const description = normalize(input.description);
      const name = normalize(input.item_name);
      const alignedItem = group.length === rfqItemCount ? records[itemIndex] : null;
      const alignedInput = alignedItem && normalizeComparisonItem(alignedItem.rfq_data);
      const alignedMatch = alignedInput && name && normalize(alignedInput.item_name) === name
        && (!description || normalize(alignedInput.description) === description);
      let matches = records.slice(0, rfqItemCount).filter((entry) => name && normalize(normalizeComparisonItem(entry.rfq_data).item_name) === name);
      if (matches.length > 1 && description) {
        const exactDescription = matches.filter((entry) => normalize(normalizeComparisonItem(entry.rfq_data).description) === description);
        if (exactDescription.length) matches = exactDescription;
      }
      if (!matches.length) matches = records.slice(0, rfqItemCount).filter((entry) => description && normalize(normalizeComparisonItem(entry.rfq_data).description) === description);
      const quotations = getRecordQuotations(record)
        .map((quote) => ({ ...quote, _comparison_document: documentIndex }));
      const target = alignedMatch ? alignedItem : matches.find((entry) => !matchedItems.has(entry)) || (matches.length === 1 ? matches[0] : null);
      if (target) {
        target.quotations.push(...quotations);
        matchedItems.add(target);
      } else records.push({ rfq_data: record.rfq_data, quotations: [...quotations] });
    });
  });
  return records;
};

const buildItemComparison = (records) => {
  const vendors = new Map();
  const rows = records.filter((record) => record?.rfq_data).map((record, rowIndex) => {
    const item = normalizeComparisonItem(record.rfq_data);
    const quotes = new Map();
    (Array.isArray(record.quotations) ? record.quotations : []).filter(Boolean).forEach((raw, quoteIndex) => {
      const quote = normalizeComparisonItem(raw);
      const name = [quote.vendor_name, quote.vendor, quote.company_name].find(isQuotedValue);
      const documentIndex = Number(raw?._comparison_document);
      const fallbackName = Number.isInteger(documentIndex)
        ? `Quotation ${documentIndex + 1}`
        : `Quotation ${rowIndex + 1}.${quoteIndex + 1}`;
      const key = name
        ? String(name).trim().toLowerCase().replace(/\s+/g, " ")
        : `quotation-document-${Number.isInteger(documentIndex) ? documentIndex : `${rowIndex}-${quoteIndex}`}`;
      vendors.set(key, name || fallbackName);
      if (!quotes.has(key)) quotes.set(key, []);
      quotes.get(key).push(quote);
    });
    return { specification: item.description, items: [item], quotes };
  });
  return { rows, vendors: [...vendors], detailFields: ["currency", "warranty", "delivery_terms", "payment_terms"] };
};

const specificationEntries = (item) => item?.specification && typeof item.specification === "object" && !Array.isArray(item.specification)
  ? Object.entries(item.specification) : [];
const specificationKey = (key) => key.trim().toLowerCase().replace(/[_\s]+/g, " ");

const buildComparisonRows = (rows, files) => {
  const items = Array.isArray(rows) ? rows.filter((row) => row && typeof row === "object") : [];
  const documents = Array.isArray(files) ? files : [];
  const rfq = documents.find((file) => String(file?.Title || "").trim().toLowerCase() === "rfq")
    || documents[0];
  const input = typeof rfq?.Input_data === "string" ? rfq.Input_data.trim() : "";
  // Split numbered requirements, preserving decimals and numbers inside specifications.
  const requirements = input
    .split(/(?:^|[,;\n]\s*)\d+[.)]\s*(?=[A-Za-z])/)
    .map((value) => value.trim())
    .filter(Boolean);
  const normalize = (value) => String(value || "").toLowerCase()
    .replace(/[–—]/g, "-").replace(/[^a-z0-9-]/g, "");
  const rfqSpecifications = items.map((item) => {
    const description = normalize(item.description);
    if (!description || !isQuotedValue(item.description)) return "RFQ match unavailable";
    const matches = requirements.filter((requirement) => {
      const specification = normalize(requirement);
      return specification && description.includes(specification);
    });
    // Identical requirements can repeat; do not guess between different specifications.
    const uniqueMatches = [...new Map(matches.map((value) => [normalize(value), value])).values()];
    return uniqueMatches.length === 1 ? uniqueMatches[0] : "RFQ match unavailable";
  });

  const vendors = new Map();
  const groups = new Map();
  const mainFields = new Set(["description", "quantity", "model", "unit_price", "total_price", "uom", "unit", "required_qty", "required_quantity"]);
  const detailFields = [...new Set([
    "company_name", "brand", "currency", "warranty", "delivery_terms", "payment_terms",
    ...items.flatMap((item) => Object.keys(item).filter((key) => !mainFields.has(key) && !/evaluation|technical_superiority|price_comparison/i.test(key))),
  ])];

  items.forEach((item, index) => {
    const vendorName = [item.vendor_name, item.company_name, item.brand].find(isQuotedValue);
    const vendorKey = vendorName ? String(vendorName).trim().toLowerCase() : `unknown-${index}`;
    vendors.set(vendorKey, vendorName || "Vendor not identified");
    const specification = rfqSpecifications[index];
    // Keep unmatched quotations separate instead of combining unrelated products.
    const groupKey = specification === "RFQ match unavailable" ? `unmatched-${index}` : normalize(specification);
    if (!groups.has(groupKey)) groups.set(groupKey, { specification, quotes: new Map(), items: [] });
    const group = groups.get(groupKey);
    group.items.push(item);
    if (!group.quotes.has(vendorKey)) group.quotes.set(vendorKey, []);
    group.quotes.get(vendorKey).push(item);
  });

  return { vendors: [...vendors], detailFields, rows: [...groups.values()] };
};

const quotePriceCurrency = (value, currency) => {
  const raw = isQuotedValue(currency) ? String(currency).trim().toUpperCase()
    : String(value ?? "").trim().match(/^(INR|AED|USD|EUR|GBP|RS\.?|₹|€|£|\$)|(?:\s)(INR|AED|USD|EUR|GBP|RS\.?|₹|€|£|\$)$/i)?.[0]?.trim().toUpperCase();
  return ({ RS: "INR", "RS.": "INR", "₹": "INR", "€": "EUR", "£": "GBP", "$": "USD" })[raw] || raw || null;
};

const numericQuotePrice = (value) => {
  if (!isQuotedValue(value)) return null;
  const text = String(value).trim().replace(/^(INR|AED|USD|EUR|GBP|RS\.?|₹|€|£|\$)\s*/i, "")
    .replace(/\s*(INR|AED|USD|EUR|GBP|RS\.?|₹|€|£|\$)$/i, "").replace(/,/g, "").trim();
  if (!/^\d+(?:\.\d+)?$/.test(text)) return null;
  const number = Number(text);
  return Number.isFinite(number) ? number : null;
};

const priceRanking = (row, field, quote) => {
  if (!field.includes("price") || !row.items.length) return null;
  const entries = [...row.quotes].flatMap(([vendor, quotes]) => quotes.map((item) => ({
    vendor, item, price: numericQuotePrice(item[field]),
    currency: quotePriceCurrency(item[field], item.currency),
  }))).filter((entry) => entry.price !== null);
  const current = entries.find((entry) => entry.item === quote);
  if (!current) return null;
  if (new Set(entries.map((entry) => entry.currency).filter(Boolean)).size > 1) return null;
  const prices = [...new Set(entries.map((entry) => entry.price))].sort((a, b) => a - b);
  const rank = prices.indexOf(current.price) + 1;
  return { rank, provisional: entries.some((entry) => !entry.currency) };
};

const commonQuotationValues = (rows, vendor, field) => {
  const entries = rows.flatMap((row, index) => {
    const quotes = row.quotes.get(vendor);
    if (!quotes?.length) return [];
    const values = [...new Set(quotes.map((quote) =>
      formatComparisonValue(field, quote[field], quote.currency)
    ))];
    return [{ item: index + 1, value: values.join(" / ") }];
  });
  const uniqueValues = [...new Set(entries.map(({ value }) => value))];
  return uniqueValues.length <= 1
    ? uniqueValues[0] || "Not Quoted"
    : entries.map(({ item, value }) => `Item ${item}: ${value}`).join("\n");
};

const requirementValue = (items, keys) => {
  const values = [...new Set(items.flatMap((item) => keys.map((key) => item[key])).filter(isQuotedValue))];
  return values.length ? values.join(" / ") : "Not Quoted";
};

const formatComparisonValue = (key, value, currency) => {
  if (Array.isArray(value)) {
    return value.length ? value.map((entry) => formatComparisonValue(key, entry, currency)).join(", ") : "Not Quoted";
  }
  if (value && typeof value === "object") {
    return Object.entries(value)
      .map(([field, entry]) => `${field}: ${formatComparisonValue(field, entry, value.currency ?? currency)}`)
      .join("\n") || "Not Quoted";
  }
  return /(^|[_\s])(price|amount|cost|total)([_\s]|$)/i.test(key)
    ? formatPrice(value, isQuotedValue(currency) ? currency : "")
    : String(displayValue(value));
};

const vendorHasItemWarranties = (rows, vendor) => new Set(
  rows.flatMap((row) => row.quotes.get(vendor) || []).map((quote) =>
    isQuotedValue(quote.warranty) ? String(quote.warranty).trim().toLowerCase().replace(/\s+/g, " ") : "not quoted"
  )
).size > 1;

const RankedQuoteValue = ({ row, field, quote }) => {
  const ranking = priceRanking(row, field, quote);
  const value = formatComparisonValue(field, quote[field], quote.currency).replace(/^Not Quoted$/, "-");
  if (!ranking) return <div>{value}</div>;
  return (
    <div className={classes.rankedPrice} data-price-rank={Math.min(ranking.rank, 4)}>
      <span className={classes.inlinePriceRank}>L{ranking.rank}</span>
      <span className={classes.priceAmount}>{value}</span>
    </div>
  );
};

// Keep numeric quote fields compact so more suppliers fit on screen, while
// leaving enough room for formatted currency values and the ranking badge.
const itemColumnWidth = (field) => ({ description: 240, model: 110, brand: 96, warranty: 110, quantity: 64, unit_price: 170, total_price: 170 })[field] || 110;
const normalizeHistoryMatchValue = (value) => String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
const findPreviousPurchase = (item, previousPurchases) => {
  const itemToken = normalizeHistoryMatchValue(item?.item_token || item?.itemToken || item?.token);
  const itemCode = normalizeHistoryMatchValue(item?.item_code || item?.itemcode);
  const itemName = normalizeHistoryMatchValue(item?.item_name || item?.itemname);
  const itemDescription = normalizeHistoryMatchValue(item?.description);
  const purchases = Array.isArray(previousPurchases) ? previousPurchases : [];
  const matchedPurchase = purchases.find((purchase) =>
    (itemToken && itemToken === normalizeHistoryMatchValue(purchase?.comparison_item_token)) ||
    (itemCode && itemCode === normalizeHistoryMatchValue(purchase?.item_code)) ||
    (itemName && itemName === normalizeHistoryMatchValue(purchase?.item_name)) ||
    (itemDescription && itemDescription === normalizeHistoryMatchValue(purchase?.description))
  );
  return matchedPurchase || (purchases.length === 1 ? purchases[0] : null);
};
const PreviousPurchasePrice = ({ item, previousPurchases }) => {
  const purchase = findPreviousPurchase(item, previousPurchases);
  if (!purchase?.unit_price) return <span className={classes.missingQuote}>No previous purchase history</span>;
  return <div style={{ whiteSpace: "normal", overflowWrap: "anywhere", textAlign: "left" }}>
    <strong>{formatPrice(purchase.unit_price, purchase.currency)}</strong>
    {purchase.quantity && <small style={{ display: "block", marginTop: 4 }}>Qty: {purchase.quantity}{purchase.unit ? ` ${purchase.unit}` : ""}</small>}
    {purchase.previous_vendor_name && <small style={{ display: "block", marginTop: 4 }}>{purchase.previous_vendor_name}</small>}
    {purchase.purchase_order_number && <small style={{ display: "block" }}>PO: {purchase.purchase_order_number}</small>}
  </div>;
};
const ItemSpecificationTable = ({ comparison, columns, detailFields, previousPurchases, exporting = false }) => {
  const tableRef = useRef(null);
  useEffect(() => {
    const table = tableRef.current;
    const header = table?.tHead;
    const viewport = table?.closest(`.${classes.itemTableViewport}`);
    if (!header || !viewport || exporting) return;
    // Keep the measured header in normal flow; a separate fixed copy cannot
    // affect its scroll position or trigger a measurement/transform feedback loop.
    const overlay = document.createElement("div");
    overlay.className = classes.fixedColumnHeader;
    overlay.setAttribute("aria-hidden", "true");
    const headerTable = table.cloneNode(false);
    headerTable.removeAttribute("id");
    headerTable.appendChild(table.querySelector("colgroup").cloneNode(true));
    const head = header.cloneNode(true);
    headerTable.appendChild(head);
    overlay.appendChild(headerTable);
    document.body.appendChild(overlay);
    let frame;
    const dashboardHeader = document.getElementsByClassName(dashboardClasses.HeaderBar)[0];
    const update = () => {
      const headerRect = header.getBoundingClientRect();
      const viewportRect = viewport.getBoundingClientRect();
      const tableRect = table.getBoundingClientRect();
      const stickyTop = Math.max(0, dashboardHeader?.getBoundingClientRect().bottom || 0);
      const left = Math.max(0, viewportRect.left + viewport.clientLeft);
      const width = Math.max(0, Math.min(window.innerWidth, viewportRect.left + viewport.clientLeft + viewport.clientWidth) - left);
      const visible = headerRect.top < stickyTop && tableRect.bottom > stickyTop && width > 0;
      overlay.style.display = visible ? "block" : "none";
      if (!visible) return;
      overlay.style.top = `${Math.min(stickyTop, tableRect.bottom - headerRect.height)}px`;
      overlay.style.left = `${left}px`;
      overlay.style.width = `${width}px`;
      overlay.style.height = `${headerRect.height}px`;
      headerTable.style.width = `${tableRect.width}px`;
      overlay.scrollLeft = viewport.scrollLeft + Math.max(0, left - viewportRect.left - viewport.clientLeft);
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    observer?.observe(table);
    if (dashboardHeader) observer?.observe(dashboardHeader);
    update();
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      observer?.disconnect();
      overlay.remove();
    };
  }, [comparison, columns, exporting]);
  return (
  <table ref={tableRef} className={`${classes.compareResultTable} ${classes.comparisonGrid} ${classes.itemSpecificationGrid}`}
    style={{ width: 764 + [...columns.values()].flat().reduce((sum, field) => sum + itemColumnWidth(field), 0) }}>
    <colgroup>
      {[50, 175, 120, 132, 55, 92, 140].map((width, index) => <col key={`rfq-${index}`} style={{ width }} />)}
      {comparison.vendors.flatMap(([vendor]) => columns.get(vendor).map((field) => <col key={`${vendor}-${field}`} style={{ width: itemColumnWidth(field) }} />))}
    </colgroup>
    <thead>
      <CompanyQuotationHeaders comparison={comparison} columns={columns} rfqColumns={7} />
      <tr>
        {["Sl. No.", "Item Name", "Specification", "Required Value", "UOM", "Required Qty", "Previous Purchase Price"].map((label, index) => <th key={label} scope="col" data-rfq-col={index} className={`${classes.rfqHead} ${index === 6 ? classes.previousPurchaseHead : ""}`}>{label}</th>)}
        {comparison.vendors.map(([vendor], index) => columns.get(vendor).map((field, columnIndex) => (
          <th key={`${vendor}-${field}`} scope="col" data-vendor-tone={comparison.vendorTones.get(vendor)} data-vendor-start={columnIndex === 0 ? "true" : undefined}>
            {field === "description" ? "Quoted Specification" : field === "quantity" ? "Qty" : field.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())}
          </th>
        )))}
      </tr>
    </thead>
    <tbody>
      {comparison.rows.map((row, itemIndex) => {
        const item = row.items[0];
        const required = new Map(specificationEntries(item).map(([key, value]) => [specificationKey(key), value]));
        const labels = new Map();
        [item, ...[...row.quotes.values()].flat()].forEach((entry) => specificationEntries(entry).forEach(([key]) => {
          if (!labels.has(specificationKey(key))) labels.set(specificationKey(key), key);
        }));
        const fields = labels.size ? [...labels] : [["description", "Description"]];
        const optionCount = Math.max(1, ...[...row.quotes.values()].map((offers) => offers.length));
        const itemRowSpan = fields.length * optionCount;
        return Array.from({ length: optionCount }, (_, optionIndex) => fields.map(([key, label], specIndex) => (
          <tr key={`${itemIndex}-${optionIndex}-${key}`} className={specIndex === 0 ? classes.itemStartRow : undefined}>
            {specIndex === 0 && optionIndex === 0 && <><td rowSpan={itemRowSpan} data-rfq-col="0" className={classes.rfqCell}>{itemIndex + 1}</td><td rowSpan={itemRowSpan} data-rfq-col="1" className={classes.rfqCell}>
              <strong>{isQuotedValue(item.item_name) ? item.item_name : `Item ${itemIndex + 1}`}</strong>
              <div className={classes.itemDescription}>{isQuotedValue(item.description) ? item.description : "Description not provided"}</div>
            </td></>}
            {(fields.length > 1 || optionIndex === 0) && <>
              <th rowSpan={fields.length === 1 ? optionCount : 1} scope="row" data-rfq-col="2" className={classes.rfqCell}>{label}</th>
              <td rowSpan={fields.length === 1 ? optionCount : 1} data-rfq-col="3" className={classes.rfqCell}>{formatComparisonValue(key, labels.size ? required.get(key) : item.description).replace(/^Not Quoted$/, "-")}</td>
            </>}
            {specIndex === 0 && optionIndex === 0 && <>
              <td rowSpan={itemRowSpan} data-rfq-col="4" className={classes.rfqCell}>{displayValue(item.uom) === "Not Quoted" ? "-" : displayValue(item.uom)}</td>
              <td rowSpan={itemRowSpan} data-rfq-col="5" className={classes.rfqCell}>{isQuotedValue(item.required_quantity) ? item.required_quantity : "-"}</td>
              <td rowSpan={itemRowSpan} data-rfq-col="6" className={`${classes.rfqCell} ${classes.priceCell} ${classes.previousPurchaseCell}`}><PreviousPurchasePrice item={item} previousPurchases={previousPurchases} /></td>
            </>}
            {comparison.vendors.map(([vendor], vendorIndex) => {
              const offers = row.quotes.get(vendor) || [];
              return columns.get(vendor).map((field, columnIndex) => {
                if (field !== "description" && specIndex !== 0) return null;
                return <td key={`${vendor}-${field}`} rowSpan={field === "description" ? 1 : fields.length}
                  className={field.includes("price") ? classes.priceCell : undefined}
                  data-column={field.replace(/_/g, "-")}
                  data-vendor-tone={comparison.vendorTones.get(vendor)} data-vendor-start={columnIndex === 0 ? "true" : undefined}>
                  {offers[optionIndex] ? [offers[optionIndex]].map((quote) => {
                    const value = labels.size ? specificationEntries(quote).find(([name]) => specificationKey(name) === key)?.[1] : quote.item_name;
                    return <div key={optionIndex} className={classes.itemOffer}>
                      {field === "description" && optionCount > 1 && specIndex === 0 && <div className={classes.optionLabel}>Item {optionIndex + 1}</div>}
                      {field === "description" && specIndex === 0 && labels.size > 0 && (
                        <div className={classes.quotedItemContext}>
                          {isQuotedValue(quote.item_name) && <strong>{quote.item_name}</strong>}
                        </div>
                      )}
                      {field === "description" ? formatComparisonValue(key, value).replace(/^Not Quoted$/, "-") : <RankedQuoteValue row={row} field={field} quote={quote} />}
                    </div>;
                  }) : "-"}
                </td>;
              });
            })}
          </tr>
        )));
      })}
      <tr className={classes.termsSection}><th colSpan={7 + [...columns.values()].reduce((sum, fields) => sum + fields.length, 0)}><span className={classes.termsSectionTitle}>Vendor details & commercial terms</span></th></tr>
      {detailFields.map((field) => <tr key={field}>
        <th colSpan={7} scope="row" className={classes.detailLabel}>{field.replace(/_/g, " ")}</th>
        {comparison.vendors.map(([vendor], index) => <td key={vendor} colSpan={columns.get(vendor).length} data-vendor-tone={comparison.vendorTones.get(vendor)} data-vendor-start="true" style={{ whiteSpace: "pre-wrap" }}>
          {field === "warranty" && columns.get(vendor).includes("warranty") ? "See item warranties above" : commonQuotationValues(comparison.rows, vendor, field).replace(/Not Quoted/g, "-")}
        </td>)}
      </tr>)}
    </tbody>
  </table>
);
};

export default function ComparedTable({
  data,
  compareTitle,
  compareJsonFile,
  backButtonStatus = true,
  showHeader = true,
  isRefetchData = false,
  isLoading = false,
  handleRefetchData,
  isAutoRefresh = false,
  autoRefreshIntervalMs = 60 * 1000,
  isPending = false,
  pendingText = "Comparison is in progress. Please wait...",
  previousPurchases = [],
}) {

  console.log({ data })
  const customTheme = {
    tree: {
      backgroundColor: "transparent",
      fontSize: "14px",
    },
    nestedNode: {
      color: "#343434", // Color for nested nodes
    },
    value: {
      color: "#343443", // Color for values
    },
    key: {
      color: "#343434", // Color for keys
    },
  };
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    maxWidth: "900px",
    height: "calc(70vh - 30px)",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 1,
    p: 4,
  };
  // Try multiple possible data paths
  const dataArray = data?.json_data || data?.datas || (Array.isArray(data) ? data : []);
  console.log("dataArray:", dataArray);
  const itemRecords = useMemo(
    () => addQuotationOutputFallback(getItemRecords(dataArray), compareJsonFile),
    [dataArray, compareJsonFile],
  );
  const isItemResponse = itemRecords !== null;
  const exportData = useMemo(() => isItemResponse
    ? itemRecords.flatMap((record, index) => [
      { serial_number: `RFQ Item ${index + 1}`, ...normalizeComparisonItem(record.rfq_data) },
      ...(Array.isArray(record.quotations) ? record.quotations : []).filter(Boolean).map((quote) => ({ serial_number: `Item ${index + 1} quotation`, ...normalizeComparisonItem(quote) })),
    ])
    : dataArray.some(Array.isArray)
    ? dataArray.flatMap((items, index) => (Array.isArray(items) ? items : []).map((item) => ({
      serial_number: index === 0 ? "Buyer RFQ" : `Quotation ${index}`,
      ...normalizeComparisonItem(item),
    })))
    : dataArray, [dataArray, isItemResponse, itemRecords]);
  const updatedData = useDynamicTable(exportData);
  const comparisonTable = useMemo(
    () => {
      const comparison = isItemResponse ? buildItemComparison(itemRecords) : dataArray.some(Array.isArray)
        ? buildGroupedComparison(dataArray)
        : buildComparisonRows(dataArray.slice(1), data?.datas || compareJsonFile);
      const documentKey = (quote) => {
        const field = ["quotation_id", "quote_id", "document_id", "file_id", "quotation_number", "source_file", "_comparison_document"]
          .find((key) => isQuotedValue(quote[key]));
        return field ? JSON.stringify([field, String(quote[field]).trim()]) : "unidentified";
      };
      const columns = comparison.vendors.flatMap(([vendor, name]) => {
        const documents = [...new Set(comparison.rows.flatMap((row) =>
          (row.quotes.get(vendor) || []).map(documentKey)
        ))];
        if (!documents.length) documents.push("unidentified");
        return documents.map((document) => ({
          vendor, document, key: JSON.stringify([vendor, document]),
          name,
        }));
      });
      const companies = new Map();
      columns.forEach((column) => {
        const companyKey = String(column.name).trim().toLowerCase().replace(/\s+/g, " ");
        if (!companies.has(companyKey)) companies.set(companyKey, { key: companyKey, name: column.name, quotations: [] });
        companies.get(companyKey).quotations.push(column);
      });
      const orderedColumns = [...companies.values()].flatMap((company) => company.quotations);
      return {
        ...comparison,
        companies: [...companies.values()],
        vendorTones: new Map([...companies.values()].flatMap((company, index) => company.quotations.map((quote) => [quote.key, index % 2]))),
        vendors: orderedColumns.map(({ key, name }) => [key, name]),
        rows: comparison.rows.map((row) => ({
          ...row,
          quotes: new Map(columns.map(({ vendor, document, key }) => [
            key, (row.quotes.get(vendor) || []).filter((quote) => documentKey(quote) === document),
          ])),
        })),
      };
    },
    [dataArray, data?.datas, compareJsonFile, isItemResponse, itemRecords]
  );
  const vendorColumns = useMemo(() => new Map(comparisonTable.vendors.map(([vendor]) => [
    vendor, ["description", "model", "serial_number", "brand",
      ...(vendorHasItemWarranties(comparisonTable.rows, vendor) ? ["warranty"] : []),
      "quantity", "unit_price", "total_price"],
  ])), [comparisonTable]);
  const detailFields = comparisonTable.detailFields.filter((field) => field !== "brand" &&
    (field !== "warranty" || comparisonTable.vendors.some(([vendor]) => !vendorColumns.get(vendor).includes("warranty"))));
  const totalColumns = 5 + [...vendorColumns.values()].reduce((sum, columns) => sum + columns.length, 0);
  console.log("updatedData:", updatedData);
  // console.log(dataArray)
  // const updatedData = pivotComparisonData(dataArray);

  const [loadingState, setLoadingState] = useState(false);
  const [showModal, setShowModal] = useState({
    open: false,
    title: "",
    data: [],
    fileType: "",
    btnLabel: "",
  });
  const [copySuccess, setCopySuccess] = useState({
    open: false,
    vertical: "top",
    horizontal: "center",
    textMessage: "JSON copied to clipboard!",
  });
  const { vertical, horizontal, open, textMessage } = copySuccess;
  function formatDate(timestamp) {
    const date = new Date(timestamp);
    const options = {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    };
    const formatter = new Intl.DateTimeFormat("en-US", options);
    const parts = formatter.formatToParts(date);

    const month = parts.find((part) => part.type === "month").value;
    const day = parts.find((part) => part.type === "day").value;
    const year = parts.find((part) => part.type === "year").value;
    const hour = parts.find((part) => part.type === "hour").value;
    const minute = parts.find((part) => part.type === "minute").value;

    const ampm = date.getHours() >= 12 ? "PM" : "AM";
    const formattedDateString = `${day} ${month} ${year} - ${hour}:${minute} ${ampm}`;

    return formattedDateString;
  }
  useEffect(() => {
    if (!isAutoRefresh) return;
    const intervalId = setInterval(() => {
      handleRefetchData && handleRefetchData();
    }, Math.max(1000, Number(autoRefreshIntervalMs) || 60 * 1000));
    return () => {
      clearInterval(intervalId);
    };
  }, [isAutoRefresh, autoRefreshIntervalMs, handleRefetchData]);
  console.log("updatedData:", updatedData);
  let downloadPdf = () => {
    setLoadingState(true);
    // setOrganizationDetailspdfUI(true)
    const element = document.getElementById("compare_result_view");
    let options = {
      margin: [2, 5], // Set the top and bottom margins to 10mm for the first page
      jsPDF: {
        unit: updatedData?.length < 5 ? "" : "",
        format: updatedData?.length < 5 ? "a4" : [element.offsetWidth, 595.28],
        orientation: "landscape",
        margins: {
          top: 5,
          bottom: 5,
          left: 5,
          right: 5,
        },
      },
      // Callback function to modify the jsPDF object before rendering the PDF
      onBeforeSendPDF: function (pdf, renderer, options) {
        // Set the top and bottom margins to 20mm for the second page and beyond
        pdf.setPage(options.pageNumber - 1);
        if (options.pageNumber > 1) {
          pdf.setPageMargins(10, 50, 10);
        }
      },
      filename: `compared.pdf`,
    };
    setTimeout(() => {
      html2pdf().set(options).from(element).save();
      setLoadingState(false);
      // setOrganizationDetailspdfUI(false)
    }, 2000);
  };
  const exportToExcel = async () => {
    const table = document.querySelector("#compareTableMain table");
    if (!table) return;
    setLoadingState(true);
    try {
      const blob = await comparisonWorkbook(table, compareTitle || "Quotation comparison");
      saveAs(blob, "compared.xlsx");
    } catch (error) {
      console.error("Comparison Excel export failed", error);
      setCopySuccess({ open: true, vertical: "top", horizontal: "center", textMessage: "Excel export failed. Please try again." });
    } finally {
      setLoadingState(false);
    }
  };
  const handleViewDetails = (data, type, modalTitle) => {
    let title;
    let buttonLabel;
    if (type === "json") {
      title = `${modalTitle} OUTPUT`;
      buttonLabel = "Copy Json";
    } else if (type === "text") {
      title = `${modalTitle} INPUT`;
      buttonLabel = "Copy text";
    } else if (type === "error") {
      title = `${modalTitle} OUTPUT`;
      buttonLabel = "Copy text";
    }
    setShowModal((state) => ({
      ...state,
      open: true,
      data: data,
      fileType: type,
      title: title,
      btnLabel: buttonLabel,
    }));
  };
  const handleCloseDetails = () => {
    setShowModal((state) => ({
      ...state,
      open: false,
      data: [],
      fileType: "",
      title: "",
      btnLabel: "",
    }));
  };
  const handleCopyJson = (type) => {
    let data =
      type === "json"
        ? JSON.stringify(showModal.data, null, 2)
        : showModal.data;
    navigator.clipboard
      .writeText(data)
      .then(() =>
        setCopySuccess((state) => ({
          ...state,
          open: true,
          textMessage: `${type === "json" ? "JSON" : "Text"
            } copied to clipboard!`,
        }))
      )
      .catch((err) => console.error("Failed to copy JSON: ", err));
  };
  return (
    <>
      <Snackbar
        open={open}
        anchorOrigin={{ vertical, horizontal }}
        autoHideDuration={2000}
        onClose={() => setCopySuccess((state) => ({ ...state, open: false }))}
        key={vertical + horizontal}
      >
        <Alert
          onClose={() => setCopySuccess((state) => ({ ...state, open: false }))}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          <Typography sx={{ fontSize: "14px" }}>{textMessage}</Typography>
        </Alert>
      </Snackbar>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loadingState}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {
        <div
          className={`${classes.compairResultCard} ${backButtonStatus ? classes.mt30 : ""
            }`}
          id="compare_result_view"
        >
          <div className={classes.compareTitle}>
            {showHeader && (
              <div className={classes.comapreTitleCreatedHistory}>
                {compareTitle?.prn_number && (
                  <p className={classes.compareTitlePrnNo}>
                    <span>PRN Number</span> {compareTitle.prn_number}
                  </p>
                )}
                {compareTitle?.created_date && (
                  <p className={classes.compareTitleCompareOn}>
                    <span>Compared On</span>{" "}
                    {compareTitle?.created_date
                      ? formatDate(compareTitle.created_date)
                      : ""}
                  </p>
                )}
              </div>
            )}
            <div className={classes.compareTitleDownloadIconWrapper}>
              {!loadingState && (
                <div style={{ display: "flex", gap: "20px" }}>
                  {updatedData?.length > 0 && (
                    <>
                      <DButton
                        label={"EXPORT REPORT"}
                        btnHeight="4rem"
                        btnWidth={"18rem"}
                        buyerBtn={true}
                        onClick={downloadPdf}
                        customWrapperClass={
                          classes.compareTitleDownloadPdfBtnWrapper
                        }
                        customClass={classes.compareTitleDownloadPdfBtn}
                      />
                      <DButton
                        label={"EXPORT EXCEL"}
                        btnHeight="4rem"
                        btnWidth={"18rem"}
                        buyerBtn={true}
                        onClick={exportToExcel}
                        customWrapperClass={
                          classes.compareTitleDownloadPdfBtnWrapper
                        }
                        customClass={classes.compareTitleDownloadPdfBtn}
                      />
                    </>
                  )}
                  {/* <DropdownBtn
                    label="DOWNLOAD AS"
                    btnHeight="4rem"
                    btnWidth="18rem"
                    loadingState={loadingState}
                    disabled={updatedData?.length === 0}
                    onClick={(fileFormat) => {
                      let fileType = fileFormat?.toLowerCase();
                      if (fileType === "pdf") {
                        downloadPdf();
                      } else if (fileType === "xlsx") {
                        exportToExcel();
                      }
                    }}
                    options={["PDF", "XLSX"]}
                    buyerBtn={true}
                  /> */}
                  {isRefetchData && (
                    <DButton
                      label={""}
                      labelImg={refreshIcon}
                      btnHeight="4rem"
                      btnWidth={"6rem"}
                      buyerBtn={true}
                      onClick={handleRefetchData}
                      tooltipMessage="Refresh"
                      customWrapperClass={
                        classes.compareTitleDownloadPdfBtnWrapper
                      }
                      customClass={classes.compareTitleDownloadPdfBtn}
                      loadingState={isLoading}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          {!loadingState && (
            <Accordion
              className={classes.accordianWraper}
              sx={{
                width: "100%",
                marginBottom: "30px",
                border: "1px solid rgba(151, 151, 151, 0.2);",
                backgroundColor: "#fff",
                boxShadow: "none",
                borderRadius: ".6rem",
                overflow: "hidden",
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{ fontSize: "2.2rem", color: "#343434" }}
                  />
                }
                aria-controls="panel1-content"
                id="panel1-header"
                sx={{
                  fontSize: "1.6rem",
                  fontFamily: "gilroySemiBold",
                  border: "none",
                  color: "rgb(58, 58, 60)",
                  minHeight: "4.8rem !important",
                }}
                className={classes.accordionSummaryWrapper}
                classes={{ content: classes.summaryContentExpand }}
              >
                View Comparison Details
              </AccordionSummary>
              <AccordionDetails
                sx={{ backgroundColor: "#fff", border: "none", padding: 0 }}
              >
                <div className={classes.comparedFiles}>
                  {compareJsonFile?.length > 0 ? (
                    compareJsonFile.map((item) => (
                      <ComparedCard
                        data={item}
                        viewDetails={handleViewDetails}
                      />
                    ))
                  ) : (
                    <p className={classes.noCompareDataAvailale}>
                      {isPending ? (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "10px",
                            justifyContent: "center",
                          }}
                        >
                          {isLoading && (
                            <CircularProgress size={16} thickness={5} />
                          )}
                          <span>{pendingText}</span>
                        </span>
                      ) : (
                        "No data available."
                      )}
                    </p>
                  )}
                </div>
              </AccordionDetails>
            </Accordion>
          )}
          <div
            className={`${classes.compareResultCardHeaderWrapper} ${loadingState ? classes.removePadding : ""
              }`}
          >
            <div className={classes.compareResultCardHeader}>
              <h4>Techno - commerical comparison</h4>
              <BrandLogo
                imgSrc={logoImg}
                customClass={classes.compareHeaderlogoWrapper}
                imgClassName={classes.compareHeaderlogo}
              />
            </div>
            <div className={classes.container}>
              <div
                className={`${classes.datatable_wrapper} ${classes.comparisonTableWrapper} ${isItemResponse ? classes.itemTableViewport : ""}`}
                id="compareTableMain"
              >
                {comparisonTable.rows.length > 0 ? isItemResponse ? (
                  <ItemSpecificationTable comparison={comparisonTable} columns={vendorColumns} detailFields={detailFields} previousPurchases={previousPurchases} exporting={loadingState} />
                ) : (
                  <table className={`${classes.compareResultTable} ${classes.comparisonGrid}`}>
                    <thead>
                      <CompanyQuotationHeaders comparison={comparisonTable} columns={vendorColumns} rfqColumns={5} />
                      <tr>
                        <th scope="col" className={`${classes.rfqHead} ${classes.serialHead}`}>Sl. No.</th>
                        <th scope="col" className={classes.rfqHead}>RFQ Specification</th>
                        <th scope="col" className={classes.rfqHead}>UOM</th>
                        <th scope="col" className={`${classes.rfqHead} ${classes.rfqQuantityHead}`}>Required Qty</th>
                        <th scope="col" className={`${classes.rfqHead} ${classes.previousPurchaseHead}`}>Previous Purchase Price</th>
                        {comparisonTable.vendors.map(([key], vendorIndex) => (
                          <React.Fragment key={key}>
                            {vendorColumns.get(key).map((field, columnIndex) => (
                              <th key={field} scope="col" data-column={field.replace(/_/g, "-")} data-vendor-tone={comparisonTable.vendorTones.get(key)}
                                data-vendor-start={columnIndex === 0 ? "true" : undefined}>
                                {field === "description" ? "Quotation Specification" : field === "quantity" ? "Qty" : field.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())}
                              </th>
                            ))}
                          </React.Fragment>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonTable.rows.map((row, index) => (
                        <React.Fragment key={index}>
                          <tr>
                            <td className={`${classes.serialCell} ${classes.rfqCell}`}>{index + 1}</td>
                            <td className={`${classes.specificationCell} ${classes.rfqCell} ${classes.rfqSpecificationCell}`}>
                              <strong title={row.items.map((item) => item.description).filter(isQuotedValue).join(" / ")}>{row.specification}</strong>
                              {dataArray.some(Array.isArray) && row.items.map((item, itemIndex) => (
                                <div key={itemIndex} style={{ marginTop: 8, fontSize: "14px", color: "#000" }}>
                                  {["brand", "model"].filter((key) => isQuotedValue(item[key])).map((key) => `${key}: ${item[key]}`).join(" · ")}
                                </div>
                              ))}
                            </td>
                            <td className={`${classes.rfqCell} ${classes.rfqUomCell}`}>{requirementValue(row.items, ["uom", "unit"]).replace(/^Not Quoted$/, "Not specified")}</td>
                            <td className={`${classes.rfqCell} ${classes.rfqQuantityCell}`}>{requirementValue(row.items, ["required_quantity", "required_qty"]).replace(/^Not Quoted$/, "Not specified")}</td>
                            <td className={`${classes.rfqCell} ${classes.priceCell} ${classes.previousPurchaseCell}`}><PreviousPurchasePrice item={row.items[0]} previousPurchases={previousPurchases} /></td>
                            {comparisonTable.vendors.map(([vendor], vendorIndex) => (
                              <React.Fragment key={vendor}>
                                {vendorColumns.get(vendor).map((field) => (
                                  <td key={field} className={`${field.includes("price") ? classes.priceCell : classes.specificationCell} ${field === "quantity" ? classes.quantityCell : ""} ${field === "model" ? classes.modelCell : ""} ${["brand", "warranty"].includes(field) ? classes.productDetailCell : ""}`}
                                    data-column={field.replace(/_/g, "-")}
                                    data-vendor-tone={comparisonTable.vendorTones.get(vendor)} data-vendor-start={field === "description" ? "true" : undefined}
                                    style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                                    {row.quotes.get(vendor)?.length ? row.quotes.get(vendor).map((quote, quoteIndex, quotes) => (
                                      <div key={quoteIndex} style={{ padding: "8px 0", borderTop: quoteIndex ? "1px solid #e2e8f0" : undefined }}>
                                        {field === "description" && quotes.length > 1 && <div className={classes.optionLabel}>Item {quoteIndex + 1}</div>}
                                        <RankedQuoteValue row={row} field={field} quote={quote} />
                                      </div>
                                    )) : <span className={classes.missingQuote}>Not quoted</span>}
                                  </td>
                                ))}
                              </React.Fragment>
                            ))}
                          </tr>
                        </React.Fragment>
                      ))}
                          <tr className={classes.termsSection}>
                            <th colSpan={totalColumns} scope="row">
                              <span className={classes.termsSectionTitle}>Vendor details & commercial terms</span>
                            </th>
                          </tr>
                          {detailFields.map((field, index) => (
                            <tr key={field}>
                              <td className={`${classes.serialCell} ${classes.rfqCell}`}>{comparisonTable.rows.length + index + 1}</td>
                              <th scope="row" className={`${classes.detailLabel} ${classes.rfqSpecificationCell}`}>{field.replace(/_/g, " ")}</th>
                              <td colSpan={3} className={`${classes.rfqCell} ${classes.rfqDetailFill}`} />
                              {comparisonTable.vendors.map(([vendor], vendorIndex) => (
                                <td key={vendor} colSpan={vendorColumns.get(vendor).length} className={classes.commonDetailValue} data-vendor-tone={comparisonTable.vendorTones.get(vendor)} data-vendor-start="true"
                                  style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                                  {field === "warranty" && vendorColumns.get(vendor).includes("warranty")
                                    ? "See item warranties above"
                                    : commonQuotationValues(comparisonTable.rows, vendor, field)}
                                </td>
                              ))}
                            </tr>
                          ))}
                    </tbody>
                  </table>
                ) : (
                  <div className={classes.compareTableNoDataText}>
                    <p>
                      {isPending ? (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "10px",
                            justifyContent: "center",
                          }}
                        >
                          {isLoading && (
                            <CircularProgress size={16} thickness={5} />
                          )}
                          <span>{pendingText}</span>
                        </span>
                      ) : (
                        "No data available."
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      }
      {/* json view modal popup*/}
      <Modal
        keepMounted
        open={showModal.open}
        aria-labelledby="keep-mounted-modal-title"
        aria-describedby="keep-mounted-modal-description"
      >
        <Box sx={style}>
          <div className={classes.jsonViewBox}>
            <Typography variant="h4" component="h4">
              {showModal.title}
            </Typography>
            {showModal.fileType === "json" ? (
              <JSONTree data={showModal.data} theme={customTheme} />
            ) : (
              <div className={classes.viewDetailsText}>
                <p>{showModal.data}</p>
              </div>
            )}
            {/* <pre>{JSON.stringify(json, null, 2)}</pre> */}
          </div>
          <div className={classes.modalFooter}>
            <DButtonOutlined
              label="Cancel"
              btnWidth={"17rem"}
              btnHeight={"4.2rem"}
              onClick={handleCloseDetails}
              buyerBtn={true}
            />
            <DButton
              label={showModal.btnLabel}
              loadingState={false}
              btnWidth={"17rem"}
              btnHeight={"4.2rem"}
              onClick={() =>
                handleCopyJson(showModal.fileType === "json" ? "json" : "text")
              }
              buyerBtn={true}
            />
          </div>
        </Box>
      </Modal>
    </>
  );
}
